import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, TrendingUp, DollarSign, AlertTriangle, Download, Plus,
  Edit2, Trash2, CheckCircle, RefreshCw, ExternalLink, Lock, Eye, EyeOff,
  MessageCircle, Phone, X, Save, ChevronDown, ChevronUp,
  BarChart2, MousePointer, Activity, Kanban, Clock, Mail, Send, Zap,
  FileText
} from 'lucide-react';
import {
  getLeads, saveLead, updateLead, deleteLead,
  getReactivationQueue, getStats, exportToCSV,
  fetchLeadsFromSheets
} from '../utils/leads';
import { getAnalytics, getMonthlyLeads } from '../utils/analytics';
import { LEAD_STATUSES, PACKAGE_VALUES } from '../utils/constants';
import './Admin.css';

const EMPTY_LEAD = { name: '', phone: '', email: '', packageInterest: '', status: 'New', source: 'manual', notes: '', followUpDate: '' };
const PKG_LABELS = { online: 'Online', inperson: 'In-Person', dropin: 'Drop-In', unsure: 'Unsure', '': '—' };
const STATUS_COLORS = { New: 'lime', Contacted: 'blue', Booked: 'purple', 'Active Member': 'green', Inactive: 'red' };

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div className={`stat-card ${color || ''}`}>
      <div className="stat-icon">{icon}</div>
      <div>
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
        {sub && <div className="stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

function LeadModal({ lead, onClose, onSave }) {
  const [form, setForm] = useState(lead || EMPTY_LEAD);
  const handleChange = (ev) => setForm((f) => ({ ...f, [ev.target.name]: ev.target.value }));
  const handleSave = () => { onSave(form); onClose(); };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{lead?.id ? 'Edit Lead' : 'Add New Lead'}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          {[
            { name: 'name', label: 'Full Name', type: 'text', placeholder: 'Jane Doe' },
            { name: 'phone', label: 'Phone', type: 'tel', placeholder: '+1 (876) 000-0000' },
            { name: 'email', label: 'Email', type: 'email', placeholder: 'email@example.com' },
          ].map((f) => (
            <div className="form-group" key={f.name}>
              <label>{f.label}</label>
              <input type={f.type} name={f.name} value={form[f.name] || ''} onChange={handleChange} placeholder={f.placeholder} />
            </div>
          ))}
          <div className="form-group">
            <label>Package Interest</label>
            <select name="packageInterest" value={form.packageInterest || ''} onChange={handleChange}>
              <option value="">Select...</option>
              <option value="online">Online ($10,000/mo)</option>
              <option value="inperson">In-Person ($15,000/mo)</option>
              <option value="dropin">Drop-In ($1,500/class)</option>
              <option value="unsure">Not sure yet</option>
            </select>
          </div>
          <div className="form-group">
            <label>Status</label>
            <select name="status" value={form.status || 'New'} onChange={handleChange}>
              {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Source</label>
            <select name="source" value={form.source || 'manual'} onChange={handleChange}>
              <option value="contact-form">Contact Form</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="instagram">Instagram</option>
              <option value="referral">Referral</option>
              <option value="manual">Manual Entry</option>
              <option value="walkin">Walk-In</option>
            </select>
          </div>
          <div className="form-group">
            <label>Follow-Up Date</label>
            <input type="date" name="followUpDate" value={form.followUpDate || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea name="notes" value={form.notes || ''} onChange={handleChange} rows={3} placeholder="Goals, concerns, availability..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave}><Save size={14} /> Save Lead</button>
        </div>
      </div>
    </div>
  );
}

// ── Follow-Up Email Modal ─────────────────────────────────────────────────────
const FOLLOWUP_PRESETS = {
  New:            (name) => `Hi ${name}! Just wanted to follow up from The Fhysique Factory. We'd love to help you reach your goals — spots are limited, so let us know if you have any questions!`,
  Contacted:      (name) => `Hi ${name}! Checking back in from The Fhysique Factory. Did you get a chance to look over the packages? Happy to answer any questions — just reply to this email.`,
  Booked:         (name) => `Hi ${name}! Looking forward to your session at The Fhysique Factory. Let me know if you need anything beforehand. See you soon!`,
  'Active Member':(name) => `Hi ${name}! Just a quick check-in from Ramone. Keep up the great work — looking forward to your continued progress!`,
  Inactive:       (name) => `Hi ${name}! It's been a while — we miss you at The Fhysique Factory! Whenever you're ready to get back on track, we're here. New session times available too!`,
};

function FollowUpModal({ lead, onClose }) {
  const [message, setMessage] = useState(
    FOLLOWUP_PRESETS[lead.status]?.(lead.name) || `Hi ${lead.name}, following up from The Fhysique Factory!`
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    if (!lead.email) { setError('This lead has no email address on file.'); return; }
    setSending(true);
    setError('');
    try {
      const res = await fetch('/api/send-followup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toEmail: lead.email, toName: lead.name, message }),
      });
      if (!res.ok) throw new Error('Send failed');
      setSent(true);
    } catch {
      setError('Send failed. Check server logs.');
    }
    setSending(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3><Mail size={16} style={{ marginRight: 8 }} />Follow-Up Email</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          {sent ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <CheckCircle size={40} style={{ color: 'var(--lime)', marginBottom: 12 }} />
              <p>Email sent to <strong>{lead.email}</strong></p>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label>To</label>
                <input type="text" value={`${lead.name} — ${lead.email || 'No email on file'}`} readOnly style={{ opacity: 0.7 }} />
              </div>
              <div className="form-group">
                <label>Message</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} style={{ resize: 'vertical' }} />
              </div>
              {error && <p style={{ color: '#ff4444', fontSize: '0.85rem', marginBottom: 8 }}>{error}</p>}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>{sent ? 'Close' : 'Cancel'}</button>
          {!sent && (
            <button className="btn btn-primary btn-sm" onClick={handleSend} disabled={sending || !lead.email}>
              {sending ? 'Sending...' : <><Send size={14} /> Send Email</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Pipeline (Kanban) ─────────────────────────────────────────────────────────
const PIPELINE_COLS = [
  { status: 'New',           color: 'lime',   label: 'New Leads' },
  { status: 'Contacted',     color: 'blue',   label: 'Contacted' },
  { status: 'Booked',        color: 'purple', label: 'Booked' },
  { status: 'Active Member', color: 'green',  label: 'Active Members' },
  { status: 'Inactive',      color: 'red',    label: 'Inactive' },
];

const PKG_SHORT = { online: 'Online', inperson: 'In-Person', dropin: 'Drop-In', unsure: 'Unsure', '': '—' };

function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function PipelineCard({ lead, onStatusChange, onEdit }) {
  const days = daysSince(lead.updatedAt);
  const stale = days !== null && days >= 7;

  return (
    <div className={`pipe-card ${stale ? 'pipe-card-stale' : ''}`}>
      <div className="pipe-card-top">
        <span className="pipe-card-name">{lead.name || '—'}</span>
        {stale && <span className="pipe-stale-badge" title={`${days} days ago`}><Clock size={11} /> {days}d</span>}
      </div>
      {lead.packageInterest && (
        <span className="pipe-pkg">{PKG_SHORT[lead.packageInterest]}</span>
      )}
      {lead.phone && (
        <div className="pipe-contact">
          <a href={`tel:${lead.phone}`} className="pipe-chip call"><Phone size={11} /> {lead.phone}</a>
          <a
            href={`https://wa.me/${lead.phone.replace(/\D/g,'')}?text=Hi ${lead.name}, following up from The Physique Factory!`}
            target="_blank" rel="noopener noreferrer"
            className="pipe-chip wa"
          ><MessageCircle size={11} /> WA</a>
        </div>
      )}
      {lead.notes && <p className="pipe-notes">{lead.notes}</p>}
      <div className="pipe-card-footer">
        <select
          className="pipe-status-select"
          value={lead.status}
          onChange={(e) => onStatusChange(lead.id, e.target.value)}
        >
          {LEAD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="pipe-edit-btn" onClick={() => onEdit(lead)}><Edit2 size={12} /></button>
      </div>
    </div>
  );
}

function PipelineTab({ leads, onStatusChange, onEdit }) {
  return (
    <div className="pipeline-wrap">
      {PIPELINE_COLS.map(col => {
        const colLeads = leads.filter(l => l.status === col.status);
        return (
          <div key={col.status} className={`pipe-col pipe-col-${col.color}`}>
            <div className="pipe-col-header">
              <span className="pipe-col-title">{col.label}</span>
              <span className="pipe-col-count">{colLeads.length}</span>
            </div>
            <div className="pipe-col-body">
              {colLeads.length === 0
                ? <div className="pipe-empty">None</div>
                : colLeads.map(lead => (
                    <PipelineCard
                      key={lead.id}
                      lead={lead}
                      onStatusChange={onStatusChange}
                      onEdit={onEdit}
                    />
                  ))
              }
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Analytics helpers ─────────────────────────────────────────────────────────
const PAGE_LABELS = {
  '/': 'Home',
  '/packages': 'Packages',
  '/contact': 'Contact',
  '/schedule': 'Schedule',
  '/book': 'Book',
};

function HBar({ label, value, max, color = 'lime', sub }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="hbar-row">
      <div className="hbar-label">{label}</div>
      <div className="hbar-track">
        <div className={`hbar-fill hbar-${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="hbar-value">{value}{sub && <span className="hbar-sub"> {sub}</span>}</div>
    </div>
  );
}

function VBars({ data, maxVal }) {
  const top = maxVal || Math.max(...data.map(d => d.count), 1);
  return (
    <div className="vbars">
      {data.map((d) => (
        <div key={d.key || d.label} className="vbar-col">
          <div className="vbar-track">
            <div
              className="vbar-fill"
              style={{ height: `${Math.round((d.count / top) * 100)}%` }}
              title={d.count}
            />
          </div>
          <div className="vbar-label">{d.label}</div>
          <div className="vbar-val">{d.count}</div>
        </div>
      ))}
    </div>
  );
}

function FunnelStep({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="funnel-step">
      <div className="funnel-bar-wrap">
        <div className={`funnel-bar funnel-${color}`} style={{ width: `${Math.max(pct, 4)}%` }} />
      </div>
      <div className="funnel-info">
        <span className="funnel-label">{label}</span>
        <span className="funnel-count">{count}</span>
        <span className="funnel-pct">{pct}%</span>
      </div>
    </div>
  );
}

function AnalyticsTab({ analytics, leads, stats }) {
  const { totalViews30, totalViews7, todayViews, sessions30, avgPerDay, pageCounts, daily, monthlyLeads } = analytics;

  // Page traffic sorted
  const pages = Object.entries(pageCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([path, count]) => ({ path, label: PAGE_LABELS[path] || path, count }));
  const maxPage = pages[0]?.count || 1;

  // Lead source breakdown
  const sourceCounts = {};
  leads.forEach(l => { sourceCounts[l.source || 'unknown'] = (sourceCounts[l.source || 'unknown'] || 0) + 1; });
  const sources = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]);
  const maxSource = sources[0]?.[1] || 1;

  // Package interest
  const pkgMap = { online: 'Online', inperson: 'In-Person', dropin: 'Drop-In', unsure: 'Unsure' };
  const pkgCounts = {};
  leads.forEach(l => { if (l.packageInterest) pkgCounts[l.packageInterest] = (pkgCounts[l.packageInterest] || 0) + 1; });
  const pkgs = Object.entries(pkgCounts).sort((a, b) => b[1] - a[1]);
  const maxPkg = pkgs[0]?.[1] || 1;

  // Conversion funnel
  const funnelSteps = [
    { label: 'New Leads', key: 'New', color: 'lime' },
    { label: 'Contacted', key: 'Contacted', color: 'blue' },
    { label: 'Booked', key: 'Booked', color: 'purple' },
    { label: 'Active Members', key: 'Active Member', color: 'green' },
  ];
  const funnelTotal = leads.length;

  // Daily trend — last 14 days for readability
  const recentDaily = daily.slice(-14).map(d => ({
    key: d.date,
    label: new Date(d.date + 'T12:00:00').toLocaleString('default', { weekday: 'short' }).slice(0, 2),
    count: d.count,
  }));

  return (
    <div className="analytics-section">
      {/* Traffic Overview */}
      <div className="an-section-title"><Activity size={16} /> Traffic Overview (Last 30 Days)</div>
      <div className="an-cards">
        <div className="an-card">
          <div className="an-card-val">{totalViews30}</div>
          <div className="an-card-lbl">Page Views</div>
        </div>
        <div className="an-card">
          <div className="an-card-val">{sessions30}</div>
          <div className="an-card-lbl">Unique Sessions</div>
        </div>
        <div className="an-card">
          <div className="an-card-val">{totalViews7}</div>
          <div className="an-card-lbl">Views This Week</div>
        </div>
        <div className="an-card">
          <div className="an-card-val">{todayViews}</div>
          <div className="an-card-lbl">Today</div>
        </div>
        <div className="an-card">
          <div className="an-card-val">{avgPerDay}</div>
          <div className="an-card-lbl">Avg / Day</div>
        </div>
      </div>

      <div className="an-grid-2">
        {/* Daily Trend */}
        <div className="an-panel">
          <div className="an-panel-title">Daily Traffic (Last 14 Days)</div>
          {recentDaily.every(d => d.count === 0)
            ? <div className="an-empty">No traffic data yet — visits will appear here once people browse the site.</div>
            : <VBars data={recentDaily} />
          }
        </div>

        {/* Conversion Funnel */}
        <div className="an-panel">
          <div className="an-panel-title">Conversion Funnel</div>
          {funnelTotal === 0
            ? <div className="an-empty">No leads yet.</div>
            : funnelSteps.map(s => (
              <FunnelStep
                key={s.key}
                label={s.label}
                count={stats.byStatus?.[s.key] || 0}
                total={funnelTotal}
                color={s.color}
              />
            ))
          }
          {funnelTotal > 0 && (
            <div className="funnel-rate">
              Overall conversion: <strong className="text-lime">{stats.conversionRate || 0}%</strong>
            </div>
          )}
        </div>
      </div>

      <div className="an-grid-2">
        {/* Traffic by Page */}
        <div className="an-panel">
          <div className="an-panel-title"><MousePointer size={14} /> Traffic by Page (30d)</div>
          {pages.length === 0
            ? <div className="an-empty">No page view data yet.</div>
            : pages.map(p => <HBar key={p.path} label={p.label} value={p.count} max={maxPage} />)
          }
        </div>

        {/* Lead Sources */}
        <div className="an-panel">
          <div className="an-panel-title">Lead Sources</div>
          {sources.length === 0
            ? <div className="an-empty">No leads yet.</div>
            : sources.map(([src, cnt]) => (
              <HBar key={src} label={src.replace('-', ' ')} value={cnt} max={maxSource} color="blue" />
            ))
          }
        </div>
      </div>

      <div className="an-grid-2">
        {/* Monthly Leads */}
        <div className="an-panel">
          <div className="an-panel-title">Monthly Leads (Last 6 Months)</div>
          <VBars data={monthlyLeads} />
        </div>

        {/* Package Interest */}
        <div className="an-panel">
          <div className="an-panel-title">Package Interest Breakdown</div>
          {pkgs.length === 0
            ? <div className="an-empty">No leads yet.</div>
            : pkgs.map(([pkg, cnt]) => (
              <HBar key={pkg} label={pkgMap[pkg] || pkg} value={cnt} max={maxPkg} color="purple"
                sub={`${Math.round((cnt / leads.length) * 100)}%`} />
            ))
          }
          {pkgs.length > 0 && (
            <div className="an-revenue-note">
              Est. monthly revenue if all converted:&nbsp;
              <strong className="text-lime">
                ${(pkgs.reduce((sum, [pkg, cnt]) => sum + cnt * (PACKAGE_VALUES[pkg] || 0), 0)).toLocaleString()} JMD
              </strong>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Billing / Invoice Tab ─────────────────────────────────────────────────────
function BillingTab({ stats }) {
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [msg, setMsg] = useState('');

  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const RETAINING_FEE = 150;
  const LATE_PCT = 1;

  // Calculate days late from 7th of current month
  const dueDate = new Date(now.getFullYear(), now.getMonth(), 7);
  const daysLate = now > dueDate ? Math.floor((now - dueDate) / 86400000) : 0;
  const lateFee = parseFloat((RETAINING_FEE * (LATE_PCT / 100) * daysLate).toFixed(2));
  const totalDue = (RETAINING_FEE + lateFee).toFixed(2);
  const retainingPaid = invoiceStatus?.status === 'paid';
  const revenueHeld = daysLate > 0 && !retainingPaid;

  const effectiveSplit = revenueHeld
    ? { ironlogic: 100, ramone: 0 }
    : { ironlogic: 33, ramone: 67 };

  const ironlogicUSD = (((stats.totalRevenue || 0) / 150) * effectiveSplit.ironlogic / 100).toFixed(2);
  const ramoneUSD = (((stats.totalRevenue || 0) / 150) * effectiveSplit.ramone / 100).toFixed(2);

  const handleGenerateInvoice = async () => {
    setGenerating(true); setMsg('');
    try {
      const res = await fetch('/api/invoice/generate', { method: 'POST' });
      const data = await res.json();
      if (data.invoice) { setInvoiceStatus(data.invoice); setMsg('Invoice generated and sent to gym owner.'); }
      else setMsg('Error: ' + (data.error || 'Unknown'));
    } catch { setMsg('Could not reach API. Check N8N_BASE_URL in .env.'); }
    setGenerating(false);
  };

  const handleMarkPaid = async () => {
    setMarkingPaid(true); setMsg('');
    try {
      const res = await fetch('/api/invoice/paid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthKey }),
      });
      const data = await res.json();
      if (data.invoice) { setInvoiceStatus(data.invoice); setMsg('Invoice marked as paid. 67% split restored.'); }
      else setMsg('Error: ' + (data.error || 'Unknown'));
    } catch { setMsg('Could not reach API.'); }
    setMarkingPaid(false);
  };

  return (
    <div className="billing-tab">
      {/* Header */}
      <div className="billing-header">
        <div className="billing-header-left">
          <Zap size={20} className="text-lime" />
          <div>
            <h3>IronLogic AI Billing</h3>
            <p className="text-muted" style={{ fontSize: '0.82rem' }}>
              Website built &amp; operated by <a href="https://ironlogic.ai" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--lime)' }}>IronLogic AI</a>.
              Revenue split: <strong className="text-lime">33% IronLogic</strong> / <strong>67% Ramone</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Split visual */}
      <div className="billing-split-card">
        <div className="bsc-title">This Month's Revenue Split ({monthKey})</div>
        <div className="bsc-bar">
          <div className="bsc-ironlogic" style={{ width: `${effectiveSplit.ironlogic}%` }}>
            {effectiveSplit.ironlogic}% IronLogic
          </div>
          {effectiveSplit.ramone > 0 && (
            <div className="bsc-ramone" style={{ width: `${effectiveSplit.ramone}%` }}>
              {effectiveSplit.ramone}% Ramone
            </div>
          )}
        </div>
        <div className="bsc-amounts">
          <div className="bsc-amount bsc-amount-lime">
            <span>IronLogic AI</span>
            <strong>${ironlogicUSD} USD</strong>
          </div>
          <div className="bsc-amount">
            <span>Ramone (Gym Owner)</span>
            <strong>${ramoneUSD} USD</strong>
          </div>
        </div>
        {revenueHeld && (
          <div className="billing-warning">
            ⚠️ Retaining fee overdue by <strong>{daysLate} day(s)</strong>.
            All revenue is being held by IronLogic until payment is received.
          </div>
        )}
      </div>

      {/* Retaining fee invoice */}
      <div className="billing-invoice-card">
        <div className="bic-title"><FileText size={15} /> Monthly Retaining Fee — ${RETAINING_FEE} USD</div>
        <div className="bic-body">
          <div className="bic-row">
            <span>Base fee</span><strong>${RETAINING_FEE} USD</strong>
          </div>
          {daysLate > 0 && (
            <div className="bic-row bic-late">
              <span>Late fee ({daysLate}d × {LATE_PCT}%)</span>
              <strong>+${lateFee} USD</strong>
            </div>
          )}
          <div className="bic-row bic-total">
            <span>Total due</span><strong className="text-lime">${totalDue} USD</strong>
          </div>
          <div className="bic-row">
            <span>Due date</span><strong>{dueDate.toDateString()} (first 7 days of month)</strong>
          </div>
          <div className="bic-row">
            <span>Status</span>
            <strong className={retainingPaid ? 'text-lime' : daysLate > 0 ? 'text-red' : 'text-muted'}>
              {retainingPaid ? '✅ Paid' : daysLate > 0 ? `⚠️ OVERDUE ${daysLate}d` : '⏳ Pending'}
            </strong>
          </div>
        </div>
        <div className="bic-actions">
          <button
            className="btn btn-primary btn-sm"
            onClick={handleGenerateInvoice}
            disabled={generating}
          >
            <Send size={13} /> {generating ? 'Sending…' : 'Send Invoice to Ramone'}
          </button>
          {!retainingPaid && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleMarkPaid}
              disabled={markingPaid}
            >
              <CheckCircle size={13} /> {markingPaid ? 'Marking…' : 'Mark as Paid'}
            </button>
          )}
        </div>
        {msg && <p className="bic-msg">{msg}</p>}
      </div>

      {/* Terms reminder */}
      <div className="billing-terms">
        <h4>Agreement Terms</h4>
        <ul>
          <li><strong>Month 1:</strong> 33% IronLogic / 67% Ramone — no retaining fee</li>
          <li><strong>Month 2+:</strong> $150 USD/month retaining fee due by the 7th</li>
          <li><strong>Late payment:</strong> +1% per day compounding on top of $150</li>
          <li><strong>Non-payment:</strong> 100% of all website revenue held by IronLogic</li>
          <li><strong>Restoration:</strong> Access + 67% split restored immediately on payment</li>
          <li>IronLogic AI retains ownership of this website, domain, and booking system</li>
        </ul>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') || '');
  const [pwInput, setPwInput] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwError, setPwError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [reactivation, setReactivation] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [modal, setModal] = useState(null); // null | 'add' | lead object
  const [emailModal, setEmailModal] = useState(null); // null | lead object
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPkg, setFilterPkg] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('leads');
  const [expandedSetup, setExpandedSetup] = useState(null);

  const refresh = useCallback(() => {
    const ls = getLeads();
    setLeads(ls);
    setStats(getStats(PACKAGE_VALUES));
    setReactivation(getReactivationQueue());
    setAnalytics({ ...getAnalytics(), monthlyLeads: getMonthlyLeads(ls) });
  }, []);

  // Restore session on mount
  useEffect(() => {
    if (token) setAuthed(true);
  }, [token]);

  // On login, pull fresh data from Google Sheets (source of truth)
  useEffect(() => {
    if (!authed || !token) return;
    refresh();
    fetchLeadsFromSheets(token).then((sheetLeads) => {
      if (sheetLeads.length > 0) {
        setLeads(sheetLeads);
        setStats(getStats(PACKAGE_VALUES));
        setReactivation(getReactivationQueue());
        setAnalytics(prev => ({ ...prev, monthlyLeads: getMonthlyLeads(sheetLeads) }));
      }
    });
  }, [authed, token, refresh]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setPwError('');
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwInput }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPwError(data.error || 'Incorrect password');
      } else {
        sessionStorage.setItem('admin_token', data.token);
        setToken(data.token);
        setAuthed(true);
        setPwInput('');
      }
    } catch {
      setPwError('Connection error. Try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSaveLead = (form) => {
    if (form.id) updateLead(form.id, form);
    else saveLead(form);
    refresh();
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this lead?')) { deleteLead(id); refresh(); }
  };

  const handleStatusChange = (id, status) => { updateLead(id, { status }); refresh(); };

  const filtered = leads.filter((l) => {
    if (filterStatus && l.status !== filterStatus) return false;
    if (filterPkg && l.packageInterest !== filterPkg) return false;
    if (search) {
      const q = search.toLowerCase();
      return (l.name || '').toLowerCase().includes(q) || (l.phone || '').includes(q) || (l.email || '').toLowerCase().includes(q);
    }
    return true;
  });

  const SETUP_SECTIONS = [
    {
      id: 'paypal',
      title: '💳 PayPal — Live Payments & 33/67 Split',
      content: (
        <div className="setup-content">
          <p>PayPal is fully wired up. Every payment is verified server-side and the <strong className="text-lime">33/67 split is calculated automatically</strong>.</p>
          <h5>How it works:</h5>
          <ol>
            <li>Client pays via PayPal on the Book page</li>
            <li>Server verifies the order is genuinely <strong>COMPLETED</strong> (fraud check)</li>
            <li>Split is calculated: <strong className="text-lime">33% IronLogic</strong> / <strong>67% Gym Owner</strong></li>
            <li>Make.com webhook fires with full transaction details</li>
            <li>You manually send the gym owner's 67% from your PayPal dashboard</li>
          </ol>
          <div className="setup-note">
            <strong>PayPal account:</strong> <code>fhysiquefactoryltd@gmail.com</code><br />
            All payments land here. Use the Billing tab to track pending payouts.
          </div>
          <div className="setup-links">
            <a href="https://www.paypal.com/mep/dashboard" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><ExternalLink size={14} /> PayPal Dashboard</a>
          </div>
        </div>
      ),
    },
    {
      id: 'cal',
      title: '📅 Cal.com — Online Booking',
      content: (
        <div className="setup-content">
          <p>Cal.com is free and open-source. Clients book directly into the gym's calendar.</p>
          <h5>Setup Steps:</h5>
          <ol>
            <li>Create a free account at <a href="https://cal.com" target="_blank" rel="noopener noreferrer">cal.com</a></li>
            <li>Connect Google Calendar or Outlook</li>
            <li>Create 3 event types: <strong>1-on-1 Consultation (60 min) — $2,000 JMD</strong>, <strong>Online Training Signup (30 min)</strong>, <strong>In-Person Trial (60 min)</strong></li>
            <li>Copy your booking link (e.g. <code>cal.com/physiqueFactory/consultation</code>)</li>
            <li>Open <code>src/utils/constants.js</code> and update the <code>calLink</code> value</li>
            <li>The Contact page will automatically embed it</li>
          </ol>
          <div className="setup-links">
            <a href="https://cal.com" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><ExternalLink size={14} /> Set Up Cal.com</a>
          </div>
        </div>
      ),
    },
    {
      id: 'email',
      title: '✉️ Email Follow-Up Sequences',
      content: (
        <div className="setup-content">
          <p>Set up automated follow-up emails so no lead goes cold.</p>
          <h5>Recommended Tool: Brevo (free — 300 emails/day)</h5>
          <ol>
            <li>Create free account at <a href="https://brevo.com" target="_blank" rel="noopener noreferrer">brevo.com</a></li>
            <li>Create a contact list called "Physique Factory Leads"</li>
            <li>Build an automation sequence:
              <ul>
                <li><strong>Day 0:</strong> "Welcome to The Physique Factory — here's what's next"</li>
                <li><strong>Day 2:</strong> "Your schedule options + how to book"</li>
                <li><strong>Day 5:</strong> "A transformation story + limited spots"</li>
                <li><strong>Day 14:</strong> Re-engagement — "Still thinking about it?"</li>
              </ul>
            </li>
            <li>Get your Brevo API key → add to <code>.env</code> as <code>REACT_APP_BREVO_KEY</code></li>
            <li>When a lead submits the contact form, trigger the automation via API</li>
          </ol>
          <div className="setup-links">
            <a href="https://brevo.com" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><ExternalLink size={14} /> Set Up Brevo</a>
            <a href="https://mailchimp.com" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><ExternalLink size={14} /> Or Use Mailchimp</a>
          </div>
        </div>
      ),
    },
    {
      id: 'revenue',
      title: '💰 Revenue Share Agreement',
      content: (
        <div className="setup-content">
          <p>Protect your 33% with a written agreement.</p>
          <h5>Key Terms to Include:</h5>
          <ul>
            <li>Site builder receives <strong>33%</strong> of all revenue from leads generated via this website</li>
            <li>Gym owner receives <strong>67%</strong> of all revenue</li>
            <li>Monthly reporting using this CRM dashboard</li>
            <li>Payment within 7 days of each month's end</li>
            <li>Site builder retains ownership of the website and domain</li>
            <li>30-day notice required to terminate agreement</li>
          </ul>
          <div className="setup-links">
            <a href="https://www.docracy.com" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><ExternalLink size={14} /> Free Contract Templates</a>
            <a href="https://www.and.co/contracts" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><ExternalLink size={14} /> AND CO Contracts</a>
          </div>
          <div className="setup-note">
            Tip: Also register the domain in your name and host it yourself. This gives you leverage if payment disputes arise.
          </div>
        </div>
      ),
    },
  ];

  if (!authed) {
    return (
      <div className="page admin-login">
        <div className="login-box">
          <Lock size={32} className="text-lime" />
          <h2>Admin Access</h2>
          <p className="text-muted">The Physique Factory Dashboard</p>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Password</label>
              <div className="pw-input-wrap">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={pwInput}
                  onChange={(e) => setPwInput(e.target.value)}
                  placeholder="Enter admin password"
                  autoFocus
                />
                <button type="button" onClick={() => setShowPw((v) => !v)}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {pwError && <span className="form-error">{pwError}</span>}
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={loginLoading}>
              {loginLoading ? 'Verifying...' : 'Enter Dashboard'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="page admin-page">
      <div className="admin-header">
        <div className="container admin-header-inner">
          <div>
            <h1>Dashboard</h1>
            <p className="text-muted">The Fhysique Factory · CRM & Revenue Tracker · <a href="https://ironlogic.ai" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--lime)', fontWeight: 700 }}>Built by IronLogic AI ⚡</a></p>
          </div>
          <div className="admin-header-actions">
            <button className="btn btn-outline btn-sm" onClick={refresh}><RefreshCw size={14} /> Refresh</button>
            <button className="btn btn-outline btn-sm" onClick={exportToCSV}><Download size={14} /> Export CSV</button>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('add')}><Plus size={14} /> Add Lead</button>
          </div>
        </div>
      </div>

      <div className="container admin-body">
        {/* Stats */}
        <div className="stats-grid">
          <StatCard icon={<Users size={24} />} label="Total Leads" value={stats.total || 0} sub={`${(stats.byStatus?.['New'] || 0)} new`} />
          <StatCard icon={<CheckCircle size={24} />} label="Paid Signups" value={(stats.byStatus?.['Paid'] || 0) + (stats.byStatus?.['Active Member'] || 0)} sub={`${stats.byStatus?.['Active Member'] || 0} active · ${stats.byStatus?.['Paid'] || 0} paid`} color="lime" />
          <StatCard icon={<DollarSign size={24} />} label="Monthly Revenue" value={`$${(stats.totalRevenue || 0).toLocaleString()}`} sub="JMD (active members)" color="lime" />
          <StatCard icon={<TrendingUp size={24} />} label="Your 33% Share" value={`$${(stats.siteOwnerRevenue || 0).toLocaleString()}`} sub={`Gym owner: $${(stats.gymOwnerRevenue || 0).toLocaleString()}`} color="lime" />
          <StatCard icon={<AlertTriangle size={24} />} label="Reactivation Queue" value={reactivation.length} sub="Inactive 30+ days" color={reactivation.length > 0 ? 'red' : ''} />
        </div>

        {/* Split visual */}
        <div className="split-bar-wrap">
          <div className="split-labels">
            <span className="text-lime">You (33%) — ${(stats.siteOwnerRevenue || 0).toLocaleString()} JMD</span>
            <span className="text-muted">Gym Owner (67%) — ${(stats.gymOwnerRevenue || 0).toLocaleString()} JMD</span>
          </div>
          <div className="split-bar">
            <div className="split-bar-you" style={{ width: '33%' }} />
            <div className="split-bar-gym" style={{ width: '67%' }} />
          </div>
        </div>

        {/* Tabs */}
        <div className="admin-tabs">
          {['leads', 'pipeline', 'reactivation', 'analytics', 'billing', 'setup'].map((t) => (
            <button
              key={t}
              className={`tab-btn ${activeTab === t ? 'active' : ''} ${t === 'billing' ? 'tab-btn-billing' : ''}`}
              onClick={() => setActiveTab(t)}
            >
              {t === 'leads' && <Users size={14} />}
              {t === 'pipeline' && <Kanban size={14} />}
              {t === 'reactivation' && <AlertTriangle size={14} />}
              {t === 'analytics' && <BarChart2 size={14} />}
              {t === 'billing' && <Zap size={14} />}
              {t === 'setup' && <ExternalLink size={14} />}
              {t.charAt(0).toUpperCase() + t.slice(1)}
              {t === 'reactivation' && reactivation.length > 0 && (
                <span className="tab-badge">{reactivation.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Leads tab */}
        {activeTab === 'leads' && (
          <div className="leads-section">
            <div className="leads-filters">
              <input
                className="filter-input"
                placeholder="Search name, phone, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">All Statuses</option>
                {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="filter-select" value={filterPkg} onChange={(e) => setFilterPkg(e.target.value)}>
                <option value="">All Packages</option>
                <option value="online">Online</option>
                <option value="inperson">In-Person</option>
                <option value="dropin">Drop-In</option>
              </select>
              <span className="leads-count">
                {filtered.length} leads &nbsp;·&nbsp;
                {stats.byStatus?.['Paid'] || 0} paid &nbsp;·&nbsp;
                {stats.byStatus?.['Active Member'] || 0} active
              </span>
            </div>

            {filtered.length === 0 ? (
              <div className="empty-state">
                <Users size={40} />
                <p>{leads.length === 0 ? 'No leads yet. Add your first lead or wait for form submissions.' : 'No leads match your filters.'}</p>
              </div>
            ) : (
              <div className="leads-table-wrap">
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Package</th>
                      <th>Status</th>
                      <th>Source</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((lead) => (
                      <tr key={lead.id}>
                        <td>
                          <div className="lead-name">{lead.name || '—'}</div>
                          {lead.notes && <div className="lead-notes">{lead.notes}</div>}
                          {lead.followUpDate && (
                            <div className="lead-followup">📅 {lead.followUpDate}</div>
                          )}
                        </td>
                        <td>
                          <div className="lead-contact">
                            {lead.phone && (
                              <a href={`tel:${lead.phone}`} className="contact-chip call">
                                <Phone size={12} /> {lead.phone}
                              </a>
                            )}
                            {lead.phone && (
                              <a
                                href={`https://wa.me/${lead.phone.replace(/\D/g, '')}?text=Hi ${lead.name}, following up from The Physique Factory!`}
                                target="_blank" rel="noopener noreferrer"
                                className="contact-chip wa"
                              >
                                <MessageCircle size={12} /> WA
                              </a>
                            )}
                          </div>
                          {lead.email && <div className="lead-email">{lead.email}</div>}
                        </td>
                        <td>
                          <span className="pkg-chip">{PKG_LABELS[lead.packageInterest] || '—'}</span>
                        </td>
                        <td>
                          <select
                            className={`status-select status-${(STATUS_COLORS[lead.status] || 'default')}`}
                            value={lead.status}
                            onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                          >
                            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td><span className="source-chip">{lead.source}</span></td>
                        <td className="date-cell">{new Date(lead.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="action-btns">
                            <button className="icon-btn edit" onClick={() => setModal(lead)} title="Edit"><Edit2 size={14} /></button>
                            {lead.email && (
                              <button className="icon-btn email" onClick={() => setEmailModal(lead)} title="Send Follow-Up Email"><Mail size={14} /></button>
                            )}
                            <button className="icon-btn delete" onClick={() => handleDelete(lead.id)} title="Delete"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Reactivation tab */}
        {activeTab === 'reactivation' && (
          <div className="reactivation-section">
            <div className="reactivation-header">
              <AlertTriangle size={20} className="text-lime" />
              <div>
                <h3>Reactivation Queue</h3>
                <p className="text-muted">Leads that haven't been updated in 30+ days. Reach out to re-engage.</p>
              </div>
            </div>
            {reactivation.length === 0 ? (
              <div className="empty-state"><CheckCircle size={40} className="text-lime" /><p>All caught up! No stale leads.</p></div>
            ) : (
              reactivation.map((lead) => (
                <div className="reactivation-card" key={lead.id}>
                  <div className="reactivation-info">
                    <strong>{lead.name}</strong>
                    <span className="text-muted">{lead.phone}</span>
                    <span className="pkg-chip">{PKG_LABELS[lead.packageInterest] || '—'}</span>
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                      Last updated: {new Date(lead.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="reactivation-actions">
                    {lead.phone && (
                      <a
                        href={`https://wa.me/${lead.phone.replace(/\D/g, '')}?text=Hi ${lead.name}! We miss you at The Fhysique Factory 💪 Ready to start your transformation?`}
                        target="_blank" rel="noopener noreferrer"
                        className="btn btn-whatsapp btn-sm"
                      >
                        <MessageCircle size={13} /> WhatsApp
                      </a>
                    )}
                    {lead.email && (
                      <button className="btn btn-outline btn-sm" onClick={() => setEmailModal(lead)}>
                        <Mail size={13} /> Email
                      </button>
                    )}
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => { updateLead(lead.id, { status: 'Contacted' }); refresh(); }}
                    >
                      Mark Contacted
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Pipeline tab */}
        {activeTab === 'pipeline' && (
          <PipelineTab leads={leads} onStatusChange={handleStatusChange} onEdit={(lead) => setModal(lead)} />
        )}

        {/* Analytics tab */}
        {activeTab === 'analytics' && analytics && (
          <AnalyticsTab analytics={analytics} leads={leads} stats={stats} />
        )}

        {/* Billing tab */}
        {activeTab === 'billing' && (
          <BillingTab stats={stats} />
        )}

        {/* Setup & Integrations tab */}
        {activeTab === 'setup' && (
          <div className="setup-section">
            <h3>Setup & Integrations</h3>
            <p className="text-muted" style={{ marginBottom: '24px' }}>Complete these to fully automate your revenue and bookings.</p>
            {SETUP_SECTIONS.map((sec) => (
              <div key={sec.id} className={`setup-card ${expandedSetup === sec.id ? 'open' : ''}`}>
                <button className="setup-toggle" onClick={() => setExpandedSetup(expandedSetup === sec.id ? null : sec.id)}>
                  <span>{sec.title}</span>
                  {expandedSetup === sec.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {expandedSetup === sec.id && <div className="setup-body">{sec.content}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lead Modal */}
      {modal && (
        <LeadModal
          lead={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSaveLead}
        />
      )}

      {/* Follow-Up Email Modal */}
      {emailModal && (
        <FollowUpModal
          lead={emailModal}
          onClose={() => setEmailModal(null)}
        />
      )}
    </div>
  );
}
